import { type JSX, type ReactNode, createContext, useContext, useEffect, useState } from 'react';
import type { Abfrageergebnis, Diagrammart } from '@crm/kontrakte';
import {
  type Bildoptionen,
  automatischeGrenzen,
  DARSTELLUNGSNAMEN,
  fuenfwerte,
  heatmapskala,
  heatmapZellen,
  hundertAnteile,
  kumulierteReihen,
  liegendeFolge,
  stufeVon,
} from '@crm/kontrakte/src/diagrammarten';
import { Lagezeichen, Legende } from '../komponenten/Diagramm';
import { useTelefon } from '../komponenten/telefon';
import { lagelegende, lagewert, zeichentext, zusatztext } from './Ergebnis';

/*
 * DIE BILDER DER DIAGRAMMARTEN (ÄA-54, AUS-34 bis AUS-41) in der Maske. Das
 * PDF zeichnet dieselben Zahlen in berichtsbilder.ts des Servers; die Zahlen
 * selbst (Anteile, laufende Summen, Skala) kommen aus @crm/kontrakte.
 *
 * AUS-41: Die Farbe kommt über `data-reihe` und `data-stufe` aus der Palette
 * (stil.css), nie als Inline-Stil. Sie ist nie der einzige Träger: Werte
 * stehen als Text im Bild, Linien unterscheiden sich im Strichmuster, jedes
 * Element ist mit der Tastatur erreichbar und nennt seine Zahlen im Tooltip.
 *
 * AUF DEM TELEFON (Auftrag CRM-106, ÄA-63): Unter 600 px (`useTelefon`)
 * zeichnen die Bilder auf einer schmalen Flaeche (`SCHMAL`), damit die
 * Achsenschrift nicht auf 6 px schrumpft; die Heatmap steht 1 : 1 und rollt
 * in sich. Den Tooltip erreicht ein Finger nicht — ein Tippen auf ein
 * Element schreibt seine Zahlen in die Kurzinfo unter dem Bild.
 */

/** Die Masse eines Bilds in Einheiten der viewBox. */
type Mass = {
  readonly breite: number;
  readonly links: number;
  readonly rechts: number;
  readonly oben: number;
  readonly unten: number;
  readonly hoehe: number;
  readonly telefon: boolean;
};

const BREIT: Mass = { breite: 640, links: 64, rechts: 16, oben: 14, unten: 40, hoehe: 280, telefon: false };
/** Auftrag CRM-106: so breit, wie das Bild bei 360 bis 390 px ungefaehr Platz hat — die Schrift steht fast 1 : 1. */
const SCHMAL: Mass = { breite: 340, links: 48, rechts: 10, oben: 14, unten: 40, hoehe: 240, telefon: true };

/** Auftrag CRM-106: so viel Platz braucht eine Beschriftung der Kategorieachse auf dem Telefon. */
const SCHMAL_BESCHRIFTUNG = 56;

type Format = (w: number) => string;

/** Auftrag CRM-106: Wohin ein Tippen die Zahlen eines Elements schreibt — nur auf dem Telefon gesetzt. */
const Kurzinfo = createContext<((text: string) => void) | null>(null);

/** Ein Element, das die Tastatur erreicht und seine Zahlen nennt — Tooltip und Vorleser zugleich. */
function Punkt({ text, children }: { readonly text: string; readonly children: ReactNode }): JSX.Element {
  const zeige = useContext(Kurzinfo);
  return (
    <g
      tabIndex={0}
      role="img"
      aria-label={text}
      className="diagrammart__element"
      {...(zeige === null ? {} : { onClick: () => zeige(text) })}
    >
      <title>{text}</title>
      {children}
    </g>
  );
}

/** Die senkrechte Skala von unten nach oben, mit runden Teilstrichen. */
function skala(m: Mass, tief: number, hoch: number): { readonly y: (w: number) => number; readonly striche: readonly number[] } {
  const innen = m.hoehe - m.oben - m.unten;
  const striche = automatischeGrenzen(Math.min(0, tief), Math.max(hoch, tief + 1), 5);
  const a = striche[0] ?? 0;
  const b = striche[striche.length - 1] ?? 1;
  return { y: (w) => m.oben + innen - ((w - a) / (b - a || 1)) * innen, striche };
}

function Werteachse({
  m,
  striche,
  y,
  format,
}: {
  readonly m: Mass;
  readonly striche: readonly number[];
  readonly y: (w: number) => number;
  readonly format: Format;
}): JSX.Element {
  return (
    <g aria-hidden="true">
      {striche.map((s) => (
        <g key={s}>
          <line className="diagrammart__gitter" x1={m.links} x2={m.breite - m.rechts} y1={y(s)} y2={y(s)} />
          <text className="diagramm__achse" x={m.links - 6} y={y(s) + 4} textAnchor="end">
            {format(s)}
          </text>
        </g>
      ))}
    </g>
  );
}

function Kategorieachse({
  m,
  namen,
  mitte,
}: {
  readonly m: Mass;
  readonly namen: readonly string[];
  readonly mitte: (i: number) => number;
}): JSX.Element {
  const innen = m.breite - m.links - m.rechts;
  /* Auftrag CRM-106: auf dem Telefon nur jede k-te Beschriftung — sonst stehen sie aufeinander. */
  const jede = m.telefon ? Math.max(1, Math.ceil(namen.length / Math.max(1, Math.floor(innen / SCHMAL_BESCHRIFTUNG)))) : 1;
  return (
    <g aria-hidden="true">
      {namen.map((n, i) =>
        i % jede === 0 ? (
          <text key={`${n}-${String(i)}`} className="diagramm__achse" x={mitte(i)} y={m.hoehe - m.unten + 16} textAnchor="middle">
            {kuerzen(n, Math.max(4, Math.floor((innen * jede) / namen.length / 7)))}
          </text>
        ) : null,
      )}
    </g>
  );
}

const kuerzen = (t: string, n: number): string => (t.length <= n ? t : `${t.slice(0, Math.max(1, n - 1))}…`);
const zahl = (w: string | null | undefined): number | null => (w == null ? null : Number(w));

/** AUS-35: je Gruppe und für das Gesamt Min, Q1, Median, Q3 und Max. */
function Boxplot({ m, e, format }: { readonly m: Mass; readonly e: Abfrageergebnis; readonly format: Format }): JSX.Element {
  const zeilen = [...e.zeilen, { ...e.gesamt, bezeichnung: 'Gesamt' }];
  const fuenf = zeilen.map((z) => fuenfwerte(z.werte[0]));
  const da = fuenf.filter((f) => f !== null);
  const { y, striche } = skala(m, Math.min(...da.map((f) => f.min), 0), Math.max(...da.map((f) => f.max), 1));
  const feld = (m.breite - m.links - m.rechts) / zeilen.length;
  const mitte = (i: number): number => m.links + feld * (i + 0.5);
  const halb = Math.min(28, feld * 0.3);
  return (
    <>
      <Werteachse m={m} striche={striche} y={y} format={format} />
      {fuenf.map((f, i) => {
        const name = zeilen[i]?.bezeichnung ?? '';
        if (f === null) return null;
        const x = mitte(i);
        const text = `${name}: Min ${format(f.min)}, Q1 ${format(f.q1)}, Median ${format(f.median)}, Q3 ${format(f.q3)}, Max ${format(f.max)}`;
        return (
          <Punkt key={`${name}-${String(i)}`} text={text}>
            <line className="diagrammart__whisker" x1={x} x2={x} y1={y(f.max)} y2={y(f.min)} />
            <line className="diagrammart__whisker" x1={x - halb / 2} x2={x + halb / 2} y1={y(f.max)} y2={y(f.max)} />
            <line className="diagrammart__whisker" x1={x - halb / 2} x2={x + halb / 2} y1={y(f.min)} y2={y(f.min)} />
            <rect
              className="diagrammart__box"
              data-reihe={i === zeilen.length - 1 ? '2' : '1'}
              x={x - halb}
              width={halb * 2}
              y={y(f.q3)}
              height={Math.max(1, y(f.q1) - y(f.q3))}
            />
            <line className="diagrammart__median" x1={x - halb} x2={x + halb} y1={y(f.median)} y2={y(f.median)} />
          </Punkt>
        );
      })}
      <Kategorieachse m={m} namen={zeilen.map((z) => z.bezeichnung)} mitte={mitte} />
    </>
  );
}

/** AUS-36: die Klassen als Säulen — ein Bild je Gruppe oder gestapelt in einem. */
function Histogramm({ m, e }: { readonly m: Mass; readonly e: Abfrageergebnis }): JSX.Element | null {
  const h = e.histogramm;
  if (h == null) return null;
  const klassen = h.klassen.map((k) => k.text);
  if (e.beschreibung.histogrammForm === 'gestapelt' && e.zeilen.length > 1) {
    const summen = klassen.map((_, k) => h.zeilen.reduce((a, z) => a + (z[k] ?? 0), 0));
    return (
      <svg className="diagrammart" viewBox={`0 0 ${String(m.breite)} ${String(m.hoehe)}`} role="group" aria-label={`Histogramm gestapelt: ${klassen.join(', ')}`}>
        <Klassensaeulen m={m} klassen={klassen} hoechst={Math.max(1, ...summen)} stapel={e.zeilen.map((z, j) => ({ name: z.bezeichnung, werte: h.zeilen[j] ?? [] }))} />
      </svg>
    );
  }
  const bilder = [{ name: 'Gesamt', werte: h.gesamt }, ...e.zeilen.map((z, j) => ({ name: z.bezeichnung, werte: h.zeilen[j] ?? [] }))];
  const hoechst = Math.max(1, ...bilder.slice(1).flatMap((b) => b.werte));
  return (
    <div className="diagrammart__vielfache">
      {bilder.map((b, i) => (
        <figure key={`${b.name}-${String(i)}`} className="diagrammart__vielfach">
          <figcaption className="diagrammart__titel">{b.name}</figcaption>
          <svg className="diagrammart" viewBox={`0 0 ${String(m.breite)} ${String(m.hoehe)}`} role="group" aria-label={`Histogramm ${b.name}`}>
            <Klassensaeulen m={m} klassen={klassen} hoechst={i === 0 ? Math.max(1, ...b.werte) : hoechst} stapel={[{ name: b.name, werte: b.werte }]} />
          </svg>
        </figure>
      ))}
    </div>
  );
}

function Klassensaeulen({
  m,
  klassen,
  hoechst,
  stapel,
}: {
  readonly m: Mass;
  readonly klassen: readonly string[];
  readonly hoechst: number;
  readonly stapel: readonly { readonly name: string; readonly werte: readonly number[] }[];
}): JSX.Element {
  const { y, striche } = skala(m, 0, hoechst);
  const feld = (m.breite - m.links - m.rechts) / Math.max(1, klassen.length);
  const anzahl: Format = (w) => String(Math.round(w));
  return (
    <>
      <Werteachse m={m} striche={striche.filter((s) => Number.isInteger(s))} y={y} format={anzahl} />
      {klassen.map((k, i) => {
        let unten = 0;
        return (
          <g key={k}>
            {stapel.map((s, j) => {
              const n = s.werte[i] ?? 0;
              const von = unten;
              unten += n;
              if (n === 0) return null;
              const hoch = y(von) - y(von + n);
              return (
                <Punkt key={`${s.name}-${String(j)}`} text={`${stapel.length > 1 ? `${s.name}, ` : ''}${k}: ${String(n)}`}>
                  <rect className="diagramm__balken" data-reihe={String((j % 10) + 1)} x={m.links + feld * i + 1} width={feld - 2} y={y(von + n)} height={hoch} />
                  <text className="diagrammart__inwert" x={m.links + feld * (i + 0.5)} y={y(von + n) + 12} textAnchor="middle">
                    {hoch >= 14 ? String(n) : ''}
                  </text>
                </Punkt>
              );
            })}
          </g>
        );
      })}
      <Kategorieachse m={m} namen={klassen} mitte={(i) => m.links + feld * (i + 0.5)} />
    </>
  );
}

/** AUS-37: die Anteile an der Zeile, je Gruppe auf 100 % gestapelt. */
function Hundert({ m, e }: { readonly m: Mass; readonly e: Abfrageergebnis }): JSX.Element {
  const { namen, zeilen } = hundertAnteile(e);
  const innen = m.hoehe - m.oben - m.unten;
  const y = (p: number): number => m.oben + innen - (p / 100) * innen;
  const feld = (m.breite - m.links - m.rechts) / Math.max(1, e.zeilen.length);
  return (
    <>
      <Werteachse m={m} striche={[0, 25, 50, 75, 100]} y={y} format={(p) => `${String(p)} %`} />
      {zeilen.map((anteile, i) => {
        let unten = 0;
        const gruppe = e.zeilen[i]?.bezeichnung ?? '';
        return (
          <g key={`${gruppe}-${String(i)}`}>
            {anteile.map((p, j) => {
              if (p === null || p === 0) return null;
              const von = unten;
              unten += p;
              const text = `${gruppe}, ${namen[j] ?? ''}: ${p.toLocaleString('de-DE')} %`;
              return (
                <Punkt key={namen[j] ?? String(j)} text={text}>
                  <rect className="diagramm__balken" data-reihe={String((j % 10) + 1)} x={m.links + feld * i + 2} width={feld - 4} y={y(von + p)} height={y(von) - y(von + p)} />
                  <text className="diagrammart__inwert" x={m.links + feld * (i + 0.5)} y={y(von + p / 2) + 4} textAnchor="middle">
                    {p >= 8 ? `${Math.round(p).toLocaleString('de-DE')} %` : ''}
                  </text>
                </Punkt>
              );
            })}
          </g>
        );
      })}
      <Kategorieachse m={m} namen={e.zeilen.map((z) => z.bezeichnung)} mitte={(i) => m.links + feld * (i + 0.5)} />
    </>
  );
}

/** Die Strichmuster der Linien — die zweite Unterscheidung neben der Farbe (AUS-41). */
const MUSTER = ['', '6 4', '2 3', '10 3 2 3'];

type Kumulinie = { readonly name: string; readonly werte: readonly (number | null)[]; readonly ton: string };

/** AUS-38: die Linien des kumulierten Bilds — laufende Summe, Vergleiche und Ziel. */
function kumulinien(e: Abfrageergebnis): readonly Kumulinie[] {
  const { haupt, vergleiche, ziel } = kumulierteReihen(e);
  return [
    { name: e.kennzahlen[0]?.name ?? '', werte: haupt, ton: '1' },
    ...vergleiche.map((v, j) => ({ name: v.name, werte: v.werte, ton: String(j + 2) })),
    ...(ziel === null ? [] : [{ name: 'Ziel kumuliert', werte: ziel, ton: 'ziel' }]),
  ];
}

/** AUS-38: die laufende Summe, der Vergleich kumuliert und das Ziel kumuliert. */
function Kumuliert({ m, e, format }: { readonly m: Mass; readonly e: Abfrageergebnis; readonly format: Format }): JSX.Element {
  const linien = kumulinien(e);
  const haupt = linien[0]?.werte ?? [];
  const alle = linien.flatMap((l) => l.werte).filter((w): w is number => w !== null);
  const { y, striche } = skala(m, Math.min(0, ...alle), Math.max(1, ...alle));
  const n = e.zeilen.length;
  const x = (i: number): number => m.links + ((m.breite - m.links - m.rechts) * (i + 0.5)) / Math.max(1, n);
  const pfad = (werte: readonly (number | null)[]): string =>
    werte.reduce<string>((p, w, i) => (w === null ? p : `${p}${p === '' ? 'M' : 'L'}${x(i).toFixed(1)},${y(w).toFixed(1)}`), '');
  const bis = haupt.reduce<number>((a, w, i) => (w === null ? a : i), -1);
  return (
    <>
      <Werteachse m={m} striche={striche} y={y} format={format} />
      {bis >= 0 && <path className="diagramm__flaeche" d={`${pfad(haupt)}L${x(bis).toFixed(1)},${y(0).toFixed(1)}L${x(0).toFixed(1)},${y(0).toFixed(1)}Z`} />}
      {linien.map((l, k) => (
        <g key={l.name} data-reihe={l.ton === 'ziel' ? undefined : l.ton}>
          <path
            className={l.ton === 'ziel' ? 'diagrammart__ziel' : 'diagramm__linie'}
            fill="none"
            d={pfad(l.werte)}
            strokeDasharray={MUSTER[k % MUSTER.length] || undefined}
          />
          {l.werte.map((w, i) =>
            w === null ? null : (
              <Punkt key={i} text={`${l.name}, ${e.zeilen[i]?.bezeichnung ?? ''}: ${format(w)}`}>
                <circle className={l.ton === 'ziel' ? 'diagrammart__zielpunkt' : 'diagramm__punkt'} cx={x(i)} cy={y(w)} r={3.5} />
                {/* Auftrag CRM-106: Der Punkt ist kleiner als ein Finger — eine unsichtbare Flaeche nimmt das Tippen an. */}
                {m.telefon && <circle className="diagramm__treffer" cx={x(i)} cy={y(w)} r={12} />}
              </Punkt>
            ),
          )}
        </g>
      ))}
      <Kategorieachse m={m} namen={e.zeilen.map((z) => z.bezeichnung)} mitte={x} />
      {/* Auftrag CRM-106: Auf dem Telefon passen drei Eintraege nicht nebeneinander — die Legende steht dann unter dem Bild. */}
      {!m.telefon && <g aria-hidden="true">
        {linien.map((l, k) => (
          <g key={l.name} transform={`translate(${String(m.links + k * 150)},${String(m.hoehe - 6)})`} data-reihe={l.ton === 'ziel' ? undefined : l.ton}>
            <line className={l.ton === 'ziel' ? 'diagrammart__ziel' : 'diagramm__linie'} x1={0} x2={22} y1={-4} y2={-4} strokeDasharray={MUSTER[k % MUSTER.length] || undefined} />
            <text className="diagramm__achse" x={28}>
              {kuerzen(l.name, 18)}
            </text>
          </g>
        ))}
      </g>}
    </>
  );
}

/** Auftrag CRM-106: die Legende des kumulierten Bilds auf dem Telefon — untereinander, mit dem Strichmuster der Linie. */
function Kumulegende({ linien }: { readonly linien: readonly Kumulinie[] }): JSX.Element {
  return (
    <ul className="diagrammart__legende" aria-hidden="true">
      {linien.map((l, k) => (
        <li key={l.name}>
          <svg width="22" height="12" data-reihe={l.ton === 'ziel' ? undefined : l.ton}>
            <line
              className={l.ton === 'ziel' ? 'diagrammart__ziel' : 'diagramm__linie'}
              x1={0}
              x2={22}
              y1={6}
              y2={6}
              strokeDasharray={MUSTER[k % MUSTER.length] || undefined}
            />
          </svg>
          {l.name}
        </li>
      ))}
    </ul>
  );
}

/** AUS-39: Gruppierung mal Aufteilung, die Farbstufe folgt dem Wert, der Wert steht in der Zelle. */
function Heatmap({ m, e, format }: { readonly m: Mass; readonly e: Abfrageergebnis; readonly format: Format }): JSX.Element {
  const zellen = heatmapZellen(e);
  const spalten = e.aufteilung?.werte ?? [];
  const s = heatmapskala(zellen.flat());
  const kopf = 140;
  /* Die Heatmap rechnet immer mit der breiten Flaeche: Auf dem Telefon steht sie 1 : 1 und rollt in sich. */
  const breit = Math.max(48, (BREIT.breite - kopf - BREIT.rechts) / Math.max(1, spalten.length));
  const hoch = 26;
  const breite = kopf + breit * spalten.length + BREIT.rechts;
  const oben = BREIT.oben;
  const hoehe = oben + 20 + hoch * zellen.length + 48;
  const bild = (
    <svg
      className="diagrammart"
      viewBox={`0 0 ${String(breite)} ${String(hoehe)}`}
      /* Auftrag CRM-106: in Pixeln statt gestreckt — `.diagrammart` setzt sonst die volle Breite. */
      {...(m.telefon ? { style: { width: `${String(breite)}px`, height: `${String(hoehe)}px` } } : {})}
      role="group"
      aria-label={`Heatmap: ${e.kennzahlen[0]?.name ?? ''}`}
    >
      <g aria-hidden="true">
        {spalten.map((w, j) => (
          <text key={w.bezeichnung} className="diagramm__achse" x={kopf + breit * (j + 0.5)} y={oben + 10} textAnchor="middle">
            {kuerzen(w.bezeichnung, Math.floor(breit / 7))}
          </text>
        ))}
      </g>
      {zellen.map((reihe, i) => {
        const gruppe = e.zeilen[i]?.bezeichnung ?? '';
        const top = oben + 20 + hoch * i;
        return (
          <g key={`${gruppe}-${String(i)}`}>
            <text className="diagramm__achse" x={kopf - 8} y={top + hoch / 2 + 4} textAnchor="end" aria-hidden="true">
              {kuerzen(gruppe, 20)}
            </text>
            {reihe.map((w, j) => {
              const stufe = w === null ? 0 : stufeVon(s.anteil(w));
              const text = `${gruppe}, ${spalten[j]?.bezeichnung ?? ''}: ${w === null ? 'kein Wert' : format(w)}`;
              return (
                <Punkt key={spalten[j]?.bezeichnung ?? String(j)} text={text}>
                  <rect className="diagrammart__zelle" data-stufe={String(stufe)} x={kopf + breit * j + 1} y={top + 1} width={breit - 2} height={hoch - 2} />
                  <text className="diagrammart__zellwert" data-stufe={String(stufe)} x={kopf + breit * (j + 0.5)} y={top + hoch / 2 + 4} textAnchor="middle">
                    {w === null ? '–' : format(w)}
                  </text>
                </Punkt>
              );
            })}
          </g>
        );
      })}
      <g transform={`translate(${String(kopf)},${String(hoehe - 30)})`} role="img" aria-label={`Skala: ${s.stufen.map((w, k) => `Stufe ${String(k + 1)} ab ${format(w)}`).join(', ')}`}>
        <text className="diagramm__achse" x={-8} y={13} textAnchor="end">
          Skala
        </text>
        {s.stufen.map((w, k) => (
          <g key={k} transform={`translate(${String(k * 96)},0)`}>
            <rect className="diagrammart__zelle" data-stufe={String(k + 1)} width={18} height={18} />
            <text className="diagramm__achse" x={24} y={13}>
              ab {format(w)}
            </text>
          </g>
        ))}
      </g>
    </svg>
  );
  /* Auftrag CRM-106: Schrift und Zellen bleiben lesbar gross; die Matrix rollt waagerecht in ihrer Huelle. */
  return m.telefon ? <div className="diagrammart__rolle">{bild}</div> : bild;
}

/**
 * AUS-40: liegende Balken, nach Wert sortiert, der Wert am Balkenende. AUS-34 (ÄA-56): die
 * Kennzahloptionen wie am stehenden Balken — waagerecht von Min bis Max,
 * Kasten Q1 bis Q3, Median als Strich, der Anteil hinter dem Wert, n in der
 * Kurzinfo. Die Achse reicht bis zum Max.
 */
function Liegend({
  m,
  e,
  format,
  optionen,
}: {
  readonly m: Mass;
  readonly e: Abfrageergebnis;
  readonly format: Format;
  readonly optionen?: Bildoptionen;
}): JSX.Element {
  const folge = liegendeFolge(e);
  const k = e.kennzahlen[0];
  const werte = e.zeilen.map((z) => zahl(z.werte[0]?.wert));
  const lagen = e.zeilen.map((z) => (optionen?.lage === true && k !== undefined ? lagewert(e, z, 0, k) : null));
  const anteile = e.zeilen.map((z) => (optionen?.anteil === true && z.werte[0]?.anteil != null ? `${ANTEIL.format(Number(z.werte[0].anteil))} %` : null));
  const zusatz = e.zeilen.map((z, i) => zusatztext(z.werte[0], lagen[i] ?? null, optionen?.anteil === true, optionen?.n === true));
  const lagezahlen = lagen.flatMap((p) => (p === null ? [] : [p.min, p.max, p.q1, p.q3, p.median].filter((v): v is number => v !== null)));
  const da = [...werte.filter((w): w is number => w !== null), ...lagezahlen];
  /* Auftrag CRM-106: auf dem Telefon schmalere Namensspalte, damit die Balken Platz behalten. */
  const kopf = m.telefon ? 110 : 170;
  const hoch = 24;
  const ende = m.breite - (m.telefon ? 70 : 80);
  const tief = Math.min(0, ...da);
  const top = Math.max(1, ...da);
  const x = (w: number): number => kopf + ((w - tief) / (top - tief || 1)) * (ende - kopf);
  const hoehe = m.oben + hoch * folge.length + 8;
  const legende = lagelegende([{ art: 'balken', lagen }]);
  const zeichen = zeichentext([{ art: 'balken', lagen, anteile }]);
  const bild = (
    <svg
      className="diagrammart"
      viewBox={`0 0 ${String(m.breite)} ${String(hoehe)}`}
      role="group"
      aria-label={`Liegende Balken: ${k?.name ?? ''}${zeichen === null ? '' : `. ${zeichen}`}`}
    >
      {folge.map((i, rang) => {
        const z = e.zeilen[i];
        const w = werte[i] ?? null;
        const y = m.oben + hoch * rang;
        const name = z?.bezeichnung ?? '';
        const p = lagen[i] ?? null;
        const rechts = Math.max(x(0), w === null ? x(0) : x(w), p?.max == null ? x(0) : x(p.max));
        return (
          <Punkt key={`${name}-${String(i)}`} text={`${name}: ${w === null ? 'kein Wert' : format(w)}${zusatz[i] == null ? '' : ` · ${zusatz[i]}`}`}>
            <text className="diagramm__achse" x={kopf - 8} y={y + hoch / 2 + 4} textAnchor="end">
              {kuerzen(name, m.telefon ? 15 : 26)}
            </text>
            {w === null ? (
              <text className="diagramm__wert" x={kopf + 4} y={y + hoch / 2 + 4}>
                –
              </text>
            ) : (
              <>
                <rect className="diagramm__balken" data-reihe="1" x={Math.min(x(0), x(w))} y={y + 3} width={Math.max(1, Math.abs(x(w) - x(0)))} height={hoch - 6} />
                {p !== null && <Lagezeichen p={p} x={y + hoch / 2} breite={hoch - 6} y={x} ton={1} liegend />}
                <text className="diagramm__wert" x={rechts + 6} y={y + hoch / 2 + 4}>
                  {format(w)}
                  {anteile[i] != null && (
                    <tspan className="diagramm__anteil" dx={6}>
                      {anteile[i]}
                    </tspan>
                  )}
                </text>
              </>
            )}
          </Punkt>
        );
      })}
    </svg>
  );
  return legende.length === 0 ? (
    bild
  ) : (
    <>
      {bild}
      <Legende eintraege={legende} />
    </>
  );
}

const ANTEIL = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function Stapellegende({ namen }: { readonly namen: readonly string[] }): JSX.Element {
  return (
    <ul className="diagrammart__legende">
      {namen.map((n, j) => (
        <li key={`${n}-${String(j)}`}>
          <svg width="12" height="12" aria-hidden="true">
            <rect className="diagramm__balken" data-reihe={String((j % 10) + 1)} width="12" height="12" />
          </svg>
          {n}
        </li>
      ))}
    </ul>
  );
}

/**
 * AUS-35 bis AUS-40: das Bild der gewählten Diagrammart. Gerufen nur mit einer
 * wirksamen Art (`wirksameDarstellung`) — Tauglichkeit und Optionen sind
 * dann geprüft.
 */
export function Diagrammartbild({
  art,
  ergebnis,
  format,
  optionen,
}: {
  readonly art: Diagrammart;
  readonly ergebnis: Abfrageergebnis;
  readonly format: Format;
  /** AUS-34 (ÄA-56): die Kennzahloptionen im Bild — bisher zeichnen sie nur die liegenden Balken mit. */
  readonly optionen?: Bildoptionen;
}): JSX.Element | null {
  const telefon = useTelefon();
  /* Auftrag CRM-106: die Zahlen des zuletzt angetippten Elements; ein neues Ergebnis leert sie. */
  const [kurzinfo, setzeKurzinfo] = useState<string | null>(null);
  useEffect(() => setzeKurzinfo(null), [art, ergebnis]);
  const bild = artbild({ art, ergebnis, format, m: telefon ? SCHMAL : BREIT, ...(optionen === undefined ? {} : { optionen }) });
  if (!telefon || bild === null) return bild;
  return (
    <Kurzinfo.Provider value={setzeKurzinfo}>
      {bild}
      <p className="diagrammart__kurzinfo" aria-hidden="true">
        {kurzinfo ?? 'Tippen Sie auf einen Balken oder Punkt, um seine Werte zu sehen.'}
      </p>
    </Kurzinfo.Provider>
  );
}

/** Das Bild selbst, in den Massen der breiten oder der schmalen Flaeche. */
function artbild({
  art,
  ergebnis,
  format,
  optionen,
  m,
}: {
  readonly art: Diagrammart;
  readonly ergebnis: Abfrageergebnis;
  readonly format: Format;
  readonly optionen?: Bildoptionen;
  readonly m: Mass;
}): JSX.Element | null {
  const name = `${DARSTELLUNGSNAMEN[art]}: ${ergebnis.kennzahlen[0]?.name ?? ''}${ergebnis.gruppierung === null ? '' : ` je ${ergebnis.gruppierung.name}`}`;
  const rahmen = (inhalt: JSX.Element): JSX.Element => (
    <svg className="diagrammart" viewBox={`0 0 ${String(m.breite)} ${String(m.hoehe)}`} role="group" aria-label={name}>
      {inhalt}
    </svg>
  );
  switch (art) {
    case 'boxplot':
      return rahmen(<Boxplot m={m} e={ergebnis} format={format} />);
    case 'histogramm':
      return (
        <>
          <Histogramm m={m} e={ergebnis} />
          {ergebnis.beschreibung.histogrammForm === 'gestapelt' && ergebnis.zeilen.length > 1 && <Stapellegende namen={ergebnis.zeilen.map((z) => z.bezeichnung)} />}
        </>
      );
    case 'hundert':
      return (
        <>
          <Stapellegende namen={hundertAnteile(ergebnis).namen} />
          {rahmen(<Hundert m={m} e={ergebnis} />)}
        </>
      );
    case 'kumuliert':
      return m.telefon ? (
        <>
          {rahmen(<Kumuliert m={m} e={ergebnis} format={format} />)}
          <Kumulegende linien={kumulinien(ergebnis)} />
        </>
      ) : (
        rahmen(<Kumuliert m={m} e={ergebnis} format={format} />)
      );
    case 'heatmap':
      return <Heatmap m={m} e={ergebnis} format={format} />;
    case 'liegend':
      return <Liegend m={m} e={ergebnis} format={format} {...(optionen === undefined ? {} : { optionen })} />;
  }
}
